import { instrumentQueryKeys } from '@/config/queryKeys';
import { apiClient } from '@/lib/api-client';
import type { InstrumentCreateRequest, InstrumentPublic, InstrumentUpdateRequest } from '@/types';

/**
 * Fetch all instruments for an organization.
 */
export async function fetchInstruments(orgId: string): Promise<InstrumentPublic[]> {
  const result = await apiClient.get<InstrumentPublic[]>(instrumentQueryKeys.endpoints.list(orgId));
  // Guard against null or undefined results (some apiClient implementations
  // may return undefined for empty responses). Treat either as empty list.
  if (result === null) {
    console.warn('[fetchInstruments] received empty response (null/undefined)', { orgId });
    return [];
  }
  return result;
}

/**
 * Create a new instrument.
 */
export async function createInstrument(orgId: string, body: InstrumentCreateRequest): Promise<InstrumentPublic> {
  const result = await apiClient.post<InstrumentPublic>(instrumentQueryKeys.endpoints.list(orgId), body);
  if (!result) throw new Error('Create instrument returned no content');
  return result;
}

/**
 * Update an instrument (PUT — partial update, all fields optional per InstrumentUpdateRequest).
 */
export async function updateInstrument(
  orgId: string,
  instrumentId: string,
  body: InstrumentUpdateRequest,
): Promise<InstrumentPublic> {
  const result = await apiClient.put<InstrumentPublic>(instrumentQueryKeys.endpoints.detail(orgId, instrumentId), body);
  if (!result) throw new Error('Update instrument returned no content');
  return result;
}

/**
 * Delete an instrument.
 */
export async function deleteInstrument(orgId: string, instrumentId: string): Promise<void> {
  await apiClient.delete(instrumentQueryKeys.endpoints.detail(orgId, instrumentId));
}
